import { acronym } from './format';

/**
 * Display-time clean-up of text the AI wrote (C2-33). The stored text is
 * never changed: these run where the text is rendered, so the record keeps
 * the model's own words and a better rule can be applied later.
 */

/** Words a Title Case writer leaves lower case; they don't count as evidence either way. */
const MINOR_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'but',
  'by',
  'for',
  'from',
  'in',
  'into',
  'nor',
  'of',
  'on',
  'or',
  'per',
  'the',
  'to',
  'via',
  'vs',
  'with',
]);

/** A word written "Like this" (or "Vendor's"): one capital, then lower case letters. */
const CAPITALISED_RE = /^[A-Z][a-z]+(?:['’][a-z]+)?$/;
/** A word written all in lower case letters. */
const LOWER_RE = /^[a-z]+(?:['’][a-z]+)?$/;
/** Splits a token into leading punctuation, the word, and trailing punctuation. */
const CORE_RE = /^([^A-Za-z0-9&]*)([A-Za-z0-9&][A-Za-z0-9&'’.-]*?)([^A-Za-z0-9&]*)$/;
/** Punctuation that ends a sentence, so the next word keeps its capital. */
const SENTENCE_END_RE = /[.!?:]["'”’)]*$/;

/**
 * Whether a text is written in Title Case: most of the words after the first
 * one start with a capital. Words that are acronyms, all capitals, mixed case
 * ("SharePoint") or numbers say nothing about the style and are not counted,
 * nor are minor words ("of", "the") when they are lower case.
 */
function looksTitleCased(words: string[]): boolean {
  let capitalised = 0;
  let lower = 0;
  for (const word of words.slice(1)) {
    for (const segment of (CORE_RE.exec(word)?.[2] ?? '').split('-')) {
      if (acronym(segment)) continue;
      if (CAPITALISED_RE.test(segment)) capitalised += 1;
      else if (LOWER_RE.test(segment) && !MINOR_WORDS.has(segment)) lower += 1;
    }
  }
  return capitalised > 0 && capitalised >= lower * 3;
}

/** One word of a Title Case text in sentence case; hyphenated words segment by segment. */
function sentenceCaseWord(core: string, sentenceStart: boolean): string {
  return core
    .split('-')
    .map((segment, i) => {
      const known = acronym(segment);
      if (known) return known;
      const first = sentenceStart && i === 0;
      if (first && LOWER_RE.test(segment)) return segment[0]!.toUpperCase() + segment.slice(1);
      if (!first && CAPITALISED_RE.test(segment)) return segment.toLowerCase();
      return segment;
    })
    .join('-');
}

/**
 * Turn an AI description written In Title Case into sentence case: "Go Live
 * Date Moved To Q3 In Usd" → "Go live date moved to Q3 in USD". Acronyms get
 * their capitals ("Usd" → "USD"), words already in capitals ("CGI") or mixed
 * case ("SharePoint", "McKinsey") are kept, and so is the first word of each
 * sentence. Text that is not Title Case comes back unchanged, so ordinary
 * prose such as "The vendor confirmed Deloitte will deliver on Monday" keeps
 * its names. A proper noun inside a Title Case text cannot be told from any
 * other word and is lowered; that is the price of the rule.
 */
export function toSentenceCase(text: string | null | undefined): string {
  if (!text) return '';
  const parts = text.split(/(\s+)/);
  if (!looksTitleCased(parts.filter((p) => p.trim().length > 0))) return text;

  let sentenceStart = true;
  return parts
    .map((part) => {
      if (part.trim().length === 0) return part;
      const m = CORE_RE.exec(part);
      const out = m ? `${m[1]}${sentenceCaseWord(m[2] ?? '', sentenceStart)}${m[3]}` : part;
      sentenceStart = SENTENCE_END_RE.test(part);
      return out;
    })
    .join('');
}

/**
 * Remove Markdown backslash escapes from a quote: the document parser writes
 * "No\." and "SOW\-QA" for "No." and "SOW-QA" (C2-33). CommonMark lets any
 * ASCII punctuation be escaped, so any backslash before one is dropped; a
 * backslash before a letter (a Windows path) is kept.
 */
export function stripMarkdownEscapes(text: string | null | undefined): string {
  if (!text) return '';
  return text.replace(/\\([!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~])/g, '$1');
}

/** A record id as the model copies it from the data block. */
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
/** "[Citations: …]", "[Citation: dev-1, edl-2]": the model's own footnote line. */
const CITATIONS_BLOCK_RE = /\s*\[\s*citations?\s*:[^\]]*\]/gi;
/** A bracket or parenthesis holding nothing but ids and their labels: "(ref_id: <uuid>)". */
const ID_GROUP_RE = new RegExp(
  `\\s*[(\\[]\\s*(?:(?:ref_id|ref|id|uuid)\\s*[:=]?\\s*)?${UUID}(?:\\s*[,;/]\\s*(?:(?:ref_id|ref|id|uuid)\\s*[:=]?\\s*)?${UUID})*\\s*[)\\]]`,
  'gi',
);
/** A labelled id in running text: "ref_id: <uuid>", "id=<uuid>". */
const LABELLED_ID_RE = new RegExp(`\\s*\\b(?:ref_id|ref|id|uuid)\\s*[:=]\\s*${UUID}`, 'gi');
/** A bare id. */
const BARE_ID_RE = new RegExp(`\\s*\\b${UUID}\\b`, 'gi');
/** The report's rollup placeholders ("{{domain:<id>}}"); their ids are not prose. */
const PLACEHOLDER_RE = /(\{\{[^{}]*\}\})/;

/**
 * Remove the ids the AI copied into its prose (C2-31): uuids (bare, labelled
 * "ref_id: …", or alone inside brackets) and "[Citations: …]" footnotes. The
 * stored text is unchanged; the citations array still carries every id for
 * the chips. Punctuation left stranded by a removal is tidied ("plan , due"
 * → "plan, due"; "()" goes). Text inside `{{…}}` placeholders is kept so the
 * report can still fill them.
 */
export function stripAiIds(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .split(PLACEHOLDER_RE)
    .map((part, i) => (i % 2 === 1 ? part : stripIdsFromProse(part)))
    .join('')
    .replace(/[ \t]+$/gm, '');
}

function stripIdsFromProse(text: string): string {
  return text
    .replace(CITATIONS_BLOCK_RE, '')
    .replace(ID_GROUP_RE, '')
    .replace(LABELLED_ID_RE, '')
    .replace(BARE_ID_RE, '')
    .replace(/\(\s*\)|\[\s*\]/g, '')
    .replace(/[ \t]+([,.;:!?)])/g, '$1')
    .replace(/\(\s+/g, '(')
    .replace(/[ \t]{2,}/g, ' ');
}

/**
 * Remove the Markdown markers the AI writes for emphasis and headings
 * (C2-33) so a screen that prints the prose as text does not show "**" and
 * "##": heading hashes at the start of a line, bold and italic pairs, inline
 * code ticks and horizontal rules. List dashes and numbers are kept: they
 * read as lists. Escaped punctuation ("\*") is left for
 * {@link stripMarkdownEscapes}, which runs after this in
 * {@link displayAiText}, so an escaped asterisk still shows as one.
 */
export function stripMarkdownMarkers(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, '')
    .replace(/^[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm, '')
    .replace(/(^|[^\\*])\*\*(?=\S)([^*\n]+?)(?<=\S)\*\*/g, '$1$2')
    .replace(/(^|[^\\_\w])__(?=\S)([^_\n]+?)(?<=\S)__(?!\w)/g, '$1$2')
    .replace(/(^|[^\\*\w])\*(?=\S)([^*\n]+?)(?<=\S)\*(?!\w)/g, '$1$2')
    .replace(/(^|[^\\_\w])_(?=\S)([^_\n]+?)(?<=\S)_(?!\w)/g, '$1$2')
    .replace(/(^|[^\\`])`([^`\n]+)`/g, '$1$2');
}

/**
 * AI prose as a screen prints it (C2-31, C2-33): without the ids the model
 * copied in, without Markdown markers, and without backslash escapes. For
 * the pulse narrative, the report sections and the proposal grid; the
 * stored text stays the model's own words.
 */
export function displayAiText(text: string | null | undefined): string {
  return stripMarkdownEscapes(stripMarkdownMarkers(stripAiIds(text))).trim();
}
